import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { labEntryActions, projectEntryActions } from '@/store';
import { TotalHoursFooter } from '../ProjectEntry/components';
import LabSection from './LabSection.jsx';
import { ProjectEntriesWrapper } from '../ProjectEntry/styled.js';
import TimeBucket from '../TimeBucket/TimeBucket.jsx';
import { projectEntriesError } from '@/shared';
import PageLoader from '../../components/PageLoader/PageLoader.jsx';
import { handleFetchError } from '../../helpers/toastrUtils.jsx';
import SelectedDaysHeader from '../Home/components/SelectedDaysHeader.jsx';
import { ButtonContainer } from './components/styled.js';
import AddLabRequest from './components/AddLabRequest.jsx';
import { isArrayWithLength } from '@/helpers';

const LabTimesheet = () => {
  const dispatch = useDispatch();

  const oid = useSelector(state => state.user.activeUser?.oid);
  const selectedDays = useSelector(state => state.projectEntry.projectEntries.selectedDays);
  const labEntriesData = useSelector(state => state.labEntry.labEntries.data);
  const labEntriesLoading = useSelector(state => state.labEntry.labEntries.isLoading);
  const totalHoursByDate = useSelector(state => state.timeBucket.totalHoursByDate);

  const [loading, setLoading] = useState(true);

  const startDate = moment(selectedDays[0]).format('YYYY-MM-DD');
  const endDate = moment(selectedDays[6]).format('YYYY-MM-DD');

  useEffect(() => {
    const shouldRun = oid && isArrayWithLength(selectedDays);
    if (!shouldRun) return;

    const runAsyncTasks = async () => {
      try {
        await dispatch(projectEntryActions.setupCategories(oid));
        await dispatch(projectEntryActions.requestMonthlyCompletion(oid, moment(selectedDays[0]).format(), 0));
        await dispatch(labEntryActions.getLabTimeEntries(oid, startDate, endDate));
      } catch (e) {
        handleFetchError(e, projectEntriesError);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    runAsyncTasks();
  }, [oid, selectedDays]);

  const totalHours = useMemo(() => {    
    const weekdates = selectedDays.map(date => moment(date).format('YYYY-MM-DD'));
    const projectSums = Array(7).fill(0);

    labEntriesData.forEach(lab => {
      lab.procedures?.forEach(procedure => {
        procedure.timeEntries.forEach(entry => {
          const entryDate = entry.timeEntry.entryDate; // Assuming entry.timeEntry.date is in YYYY-MM-DD format
          const dateIndex = weekdates.indexOf(entryDate);

          if (weekdates.indexOf(entryDate) !== -1) {
            projectSums[dateIndex] += entry.timeEntry.hours || 0;
          }
        });
      });
    });

    totalHoursByDate && Object.keys(totalHoursByDate).forEach(key => {
      const categorySums = totalHoursByDate[key];
      Object.keys(categorySums).forEach(date => {
        const dateIndex = weekdates.indexOf(date);
        if (dateIndex !== -1) {
          projectSums[dateIndex] += categorySums[date] || 0;
        }
      });
    });

    return projectSums;
  }, [labEntriesData, selectedDays, totalHoursByDate]);

  const handleAddLab = (payload) => {
    const DefaultedProceduresToBeAdded = payload.selectedProject.procedures.map((procedure) => {
      return {
        ...procedure,
        procedureId: procedure.procedureIdLegacy,
        timeEntries: []
      }
    });

    const addedLab = {
      ...payload?.selectedProject,
      procedures: DefaultedProceduresToBeAdded,
      timeEntries: [],
    };

    dispatch(labEntryActions.deleteLabEntryFromBlacklist(addedLab, oid));
  };

  const handleDeleteLab = (lab) => {
    dispatch(labEntryActions.addLabEntryToBlacklist(lab, oid));
  }

  return (
    <Fragment>
      {!loading && !labEntriesLoading ? (
        <>
          <ButtonContainer>
            <AddLabRequest onAddLab={handleAddLab} localEntries={labEntriesData} />
          </ButtonContainer>

          <SelectedDaysHeader />

          <ProjectEntriesWrapper>
            {labEntriesData.map((lab, index) => <LabSection lab={lab} procedures={lab.procedures} key={index} handleDeleteLab={handleDeleteLab} />)}
          </ProjectEntriesWrapper>

          <TimeBucket />

          <TotalHoursFooter totalHours={totalHours} applyDeletableRowStyles="true" />
        </>
      ) : (
        <PageLoader />
      )}
    </Fragment>
  )
}

export default LabTimesheet;